/**
 * The boss shell: a fixed left rail, a top bar that always carries this week's
 * live wage figure, and the routed page beneath.
 *
 * The wage pill is its own query against the CURRENT week, deliberately
 * separate from whatever week the roster page happens to be showing. A boss
 * browsing next month still wants "this week" to mean this week. It shares the
 * same query key as the roster page, so when they are looking at the current
 * week the two are literally the same cached object and cannot disagree.
 */
import { useCallback, useState } from 'react';
import { Link, Route, Switch, useLocation } from 'wouter';
import {
  CalendarDays,
  ClipboardCheck,
  LogOut,
  Moon,
  Receipt,
  Settings as SettingsIcon,
  Sun,
  SunMoon,
  Users,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, qk } from '../lib/api.ts';
import { useSession, applyTheme, readTheme, type ThemeChoice } from '../lib/session.tsx';
import { useRealtime } from '../lib/socket.ts';
import { today, weekStart } from '../lib/time.ts';
import { Avatar, TweenDuration, TweenMoney, Wordmark } from './Bits.tsx';
import RosterPage from '../pages/boss/RosterPage.tsx';
import TeamPage from '../pages/boss/TeamPage.tsx';
import TimesheetsPage from '../pages/boss/TimesheetsPage.tsx';
import PayPage from '../pages/boss/PayPage.tsx';
import PayRunPage from '../pages/boss/PayRunPage.tsx';
import SettingsPage from '../pages/boss/SettingsPage.tsx';

const NAV = [
  { href: '/', label: 'Roster', icon: CalendarDays, match: (l: string) => l === '/' || l.startsWith('/roster') },
  { href: '/team', label: 'Team', icon: Users, match: (l: string) => l.startsWith('/team') },
  { href: '/timesheets', label: 'Timesheets', icon: ClipboardCheck, match: (l: string) => l.startsWith('/timesheets') },
  { href: '/pay', label: 'Pay', icon: Receipt, match: (l: string) => l.startsWith('/pay') },
  { href: '/settings', label: 'Settings', icon: SettingsIcon, match: (l: string) => l.startsWith('/settings') },
] as const;

function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme());
  const next: Record<ThemeChoice, ThemeChoice> = { system: 'light', light: 'dark', dark: 'system' };
  const Icon = choice === 'light' ? Sun : choice === 'dark' ? Moon : SunMoon;
  const label =
    choice === 'light' ? 'Light theme' : choice === 'dark' ? 'Dark theme' : 'Theme follows your device';
  return (
    <button
      type="button"
      className="btn btn-icon btn-ghost"
      aria-label={label}
      title={`${label}|Click to switch. Three states: light, dark, and follow your device.`}
      onClick={() => {
        const n = next[choice];
        setChoice(n);
        applyTheme(n);
      }}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}

/** Always current, never the week being browsed. */
function LiveWagesPill({ slug }: { slug: string }) {
  const week = weekStart(today());
  const { data, isLoading } = useQuery({
    queryKey: qk.roster(slug, week),
    queryFn: () => api.roster(slug, week),
    staleTime: 15_000,
  });
  const totals = data?.totals;

  return (
    <div
      className="flex min-w-0 shrink items-center gap-2 px-2.5 py-1.5 sm:gap-3 sm:px-3.5"
      style={{
        borderRadius: 'var(--sp-radius-pill)',
        background: 'var(--sp-accent-soft)',
        border: '1px solid var(--sp-accent-border)',
      }}
      data-tip="This week's roster|Wages and hours for every shift currently on the grid, published or draft. It updates the moment a shift moves."
    >
      {/* The label is the first thing to go on a phone. The figures are the
          point, and the tipcard still says what they are. */}
      <span
        className="hidden text-[11px] font-bold uppercase tracking-wide sm:inline"
        style={{ color: 'var(--sp-accent-strong)' }}
      >
        This week
      </span>
      <span className="tabular whitespace-nowrap text-[14px] font-extrabold text-ink">
        {isLoading || !totals ? '...' : <TweenMoney value={totals.totalCost} short />}
      </span>
      {/* Hours are the second figure, and on a phone the pill has room for one. */}
      <span aria-hidden="true" className="hidden sm:block" style={{ width: 1, height: 14, background: 'var(--sp-accent-border)' }} />
      <span className="tabular hidden whitespace-nowrap text-[13px] font-semibold text-muted sm:inline">
        {isLoading || !totals ? '...' : <TweenDuration minutes={totals.totalMinutes} />}
      </span>
    </div>
  );
}

export default function BossShell() {
  const { tenant, me, signOut } = useSession();
  const [location] = useLocation();
  const slug = tenant?.tenantSlug ?? '';
  const onNotify = useCallback(() => undefined, []);
  const live = useRealtime(slug || null, onNotify);

  return (
    <div className="flex min-h-screen">
      <nav
        className="fixed inset-y-0 left-0 z-30 hidden flex-col md:flex"
        style={{
          width: 'var(--sp-rail)',
          background: 'var(--sp-surface)',
          borderRight: '1px solid var(--sp-border)',
        }}
        aria-label="Main"
      >
        <div className="flex items-center px-4 pb-3 pt-6">
          <Wordmark size={22} />
        </div>
        <div className="px-3 pb-2 pt-3">
          <div className="truncate px-2 text-[11px] font-bold uppercase tracking-wide text-faint">
            {tenant?.tenantName}
          </div>
        </div>
        <ul className="flex-1 space-y-1 px-3">
          {NAV.map((item) => {
            const active = item.match(location);
            const Icon = item.icon;
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="rail-link flex items-center gap-2.5 px-3 py-2.5 text-[14px] font-semibold"
                  aria-current={active ? 'page' : undefined}
                  style={{
                    borderRadius: 'var(--sp-radius-sm)',
                    background: active ? 'var(--sp-accent-soft)' : 'transparent',
                    color: active ? 'var(--sp-accent-strong)' : 'var(--sp-text-muted)',
                    boxShadow: active ? 'inset 0 0 0 1px var(--sp-accent-border)' : 'none',
                  }}
                >
                  <Icon size={17} aria-hidden="true" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-line p-3">
          <div className="flex items-center gap-2.5 px-1 py-1.5">
            <Avatar name={me?.user.name ?? '?'} size={30} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-bold text-ink">{me?.user.name}</span>
              <span className="block truncate text-[11.5px] capitalize text-faint">{tenant?.role}</span>
            </span>
            <button
              type="button"
              className="btn btn-icon btn-sm btn-ghost"
              onClick={() => void signOut()}
              title="Sign out|Ends this session on this device."
              aria-label="Sign out"
            >
              <LogOut size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col md:pl-[var(--sp-rail)]">
        <header
          className="sticky top-0 z-20 flex items-center gap-3 px-4"
          style={{
            height: 'var(--sp-topbar)',
            background: 'color-mix(in srgb, var(--sp-bg) 88%, transparent)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid var(--sp-border)',
          }}
        >
          <span className="md:hidden">
            <Wordmark size={19} />
          </span>
          <div className="flex-1" />
          <LiveWagesPill slug={slug} />
          <span
            className="flex h-[34px] w-[34px] items-center justify-center"
            data-tip={
              live === 'connected'
                ? 'Live|Roster changes from anyone else appear here as they happen.'
                : 'Not live|Reconnecting. What you see may be a few moments behind.'
            }
            aria-label={live === 'connected' ? 'Live updates connected' : 'Live updates reconnecting'}
            role="status"
            style={{ color: live === 'connected' ? 'var(--sp-accent)' : 'var(--sp-text-faint)' }}
          >
            {live === 'connected' ? <Wifi size={16} aria-hidden="true" /> : <WifiOff size={16} aria-hidden="true" />}
          </span>
          <ThemeToggle />
        </header>

        {/* Phone nav. The boss shell is desktop-first but has to survive a
            manager checking the roster on the way to work. */}
        {/* Phone nav. Five fixed columns rather than a horizontal scroller:
            a scroller put Settings off the edge of the screen with nothing to
            say it was there, so the section simply could not be found. */}
        <nav
          className="sticky top-[var(--sp-topbar)] z-10 grid border-b border-line px-1 py-1 md:hidden"
          style={{ background: 'var(--sp-surface)', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', height: 'var(--sp-phone-nav)' }}
          aria-label="Sections"
        >
          {NAV.map((item) => {
            const active = item.match(location);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                className="flex min-w-0 flex-col items-center gap-0.5 px-0.5 py-1.5 text-[10.5px] font-semibold"
                style={{
                  borderRadius: 'var(--sp-radius-sm)',
                  background: active ? 'var(--sp-accent-soft)' : 'transparent',
                  color: active ? 'var(--sp-accent-strong)' : 'var(--sp-text-muted)',
                }}
                aria-current={active ? 'page' : undefined}
              >
                <Icon size={17} aria-hidden="true" />
                <span className="w-full truncate text-center">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Keyed on the location so each section arrives with the page-in
            motion rather than swapping in place. */}
        <main key={location.split('/').slice(0, 2).join('/')} className="anim-page-in min-w-0 flex-1">
          <Switch>
            <Route path="/" component={RosterPage} />
            <Route path="/roster" component={RosterPage} />
            <Route path="/team" component={TeamPage} />
            <Route path="/timesheets" component={TimesheetsPage} />
            <Route path="/pay" component={PayPage} />
            <Route path="/pay/:id" component={PayRunPage} />
            <Route path="/settings" component={SettingsPage} />
            <Route>
              <div className="px-6 py-16 text-center">
                <h1 className="text-[17px] font-bold text-ink">Page not found</h1>
                <p className="mt-2 text-[13.5px] text-muted">
                  That address does not match anything in Sneaky Panda.
                </p>
                <Link href="/" className="btn btn-primary mt-5">
                  Back to the roster
                </Link>
              </div>
            </Route>
          </Switch>
        </main>
      </div>
    </div>
  );
}
